import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { AgenteDTO, CreateAgenteRequest, WrapperResponse } from '../../../model/kartera-api-model'
import APIInvoke from '../../../utils/APIInvoke'
import { clip, isEmail, onlyLetters } from '../../../utils/inputs'
import { ACTIVATE_PATH, errorMessage } from '../common/register-common'

export { LOGIN_PATH } from '../common/register-common'

const EMAIL_MAX = 100
const NOMBRE_MAX = 60
const APELLIDO_MAX = 45
const REGISTER_ERROR = 'No pudimos crear tu cuenta. Inténtalo de nuevo.'

export const useRegisterFormModel = () => {
    const navigate = useNavigate()

    const [email, setEmail] = useState('')
    const [nombres, setNombres] = useState('')
    const [apPat, setApPat] = useState('')
    const [apMat, setApMat] = useState('')
    const [terms, setTerms] = useState(false)
    const [tried, setTried] = useState(false)
    const [loading, setLoading] = useState(false)
    const [emailError, setEmailError] = useState('')
    const [submitError, setSubmitError] = useState('')

    const emailOk = isEmail(email)

    const invalid = {
        email: (tried && !emailOk) || Boolean(emailError),
        nombres: tried && !nombres.trim(),
        apPat: tried && !apPat.trim(),
        terms: tried && !terms,
    }

    const missing: string[] = []
    if (tried && !emailOk) missing.push('un correo válido')
    if (invalid.nombres) missing.push('tu nombre')
    if (invalid.apPat) missing.push('tu apellido paterno')
    if (invalid.terms) missing.push('aceptar los términos')

    const validateEmail = async () => {
        if (!emailOk) return
        try {
            const response: WrapperResponse<void> = await (await APIInvoke.invokeGET(`/usuarios/validar-email?email=${encodeURIComponent(email)}`)).json()
            setEmailError(response.ok ? '' : response.message || 'El email ya está registrado, intente iniciar sesión')
        } catch (error) {
            setEmailError('')
        }
    }

    const submit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (!emailOk || !nombres.trim() || !apPat.trim() || !terms) {
            setTried(true)
            return
        }
        if (emailError) return
        const request: CreateAgenteRequest = {
            correo: email,
            nombre: nombres.trim(),
            apPaterno: apPat.trim(),
            apMaterno: apMat.trim(),
        }
        setLoading(true)
        setSubmitError('')
        try {
            const response: WrapperResponse<AgenteDTO> = await (await APIInvoke.invokePOST('/agentes', request)).json()
            if (response.ok) {
                navigate({ pathname: ACTIVATE_PATH, search: `?email=${encodeURIComponent(email)}` })
            } else {
                setSubmitError(response.message || REGISTER_ERROR)
            }
        } catch (error) {
            setSubmitError(errorMessage(error, REGISTER_ERROR))
        } finally {
            setLoading(false)
        }
    }

    return {
        email,
        nombres,
        apPat,
        apMat,
        terms,
        loading,
        invalid,
        missing,
        emailError,
        submitError,
        setEmail: (value: string) => { setEmail(clip(value.trim(), EMAIL_MAX)); setEmailError('') },
        setNombres: (value: string) => setNombres(onlyLetters(value, NOMBRE_MAX)),
        setApPat: (value: string) => setApPat(onlyLetters(value, APELLIDO_MAX)),
        setApMat: (value: string) => setApMat(onlyLetters(value, APELLIDO_MAX)),
        toggleTerms: () => setTerms((value) => !value),
        validateEmail,
        submit,
    }
}
